import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPlus, 
  faEdit, 
  faEye, 
  faTrash,
  faSearch,
  faSpinner,
  faExclamationTriangle,
  faCopy,
  faDownload
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import PromptEditor from './PromptEditor';

const PromptsTab = ({ agentId }) => {
  const { token } = useAuth();
  const [prompts, setPrompts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Fetch prompts
  useEffect(() => {
    fetchPrompts();
  }, [agentId, token]);

  const fetchPrompts = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(`/api/agents/${agentId}/settings/prompts`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setPrompts(data.prompts || []);
      } else {
        throw new Error(`Failed to fetch prompts: ${response.status}`);
      }
    } catch (err) {
      setError(err.message);
      console.error('Error fetching prompts:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewPrompt = async (promptId) => {
    try {
      const response = await fetch(`/api/agents/${agentId}/settings/prompts/${promptId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSelectedPrompt(data.prompt);
        setShowViewModal(true);
      } else {
        toast.error('Failed to load prompt details');
      }
    } catch (err) {
      toast.error('Error loading prompt details');
      console.error('Error:', err);
    }
  };

  const handleEditPrompt = async (promptId) => {
    try {
      const response = await fetch(`/api/agents/${agentId}/settings/prompts/${promptId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSelectedPrompt(data.prompt);
        setShowEditModal(true);
      } else {
        toast.error('Failed to load prompt details');
      }
    } catch (err) {
      toast.error('Error loading prompt details');
      console.error('Error:', err);
    }
  };

  const copyToClipboard = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${type} copied to clipboard!`);
    } catch (err) {
      toast.error('Failed to copy to clipboard');
    }
  };

  const exportPrompt = (prompt) => {
    const exportData = {
      name: prompt.name,
      description: prompt.description,
      prompt_template: prompt.prompt_template,
      model: prompt.model,
      temperature: prompt.temperature,
      top_p: prompt.top_p,
      max_output_tokens: prompt.max_output_tokens,
      output_format: prompt.output_format,
      output_schema: prompt.output_schema,
      tool_enabled: prompt.tool_enabled,
      tool_choice: prompt.tool_choice,
      text_format_name: prompt.text_format_name,
      text_format_type: prompt.text_format_type,
      text_format_schema: prompt.text_format_schema,
      text_format_strict: prompt.text_format_strict,
      stream: prompt.stream
    };

    const dataStr = JSON.stringify(exportData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `prompt-${prompt.name.toLowerCase().replace(/\s+/g, '-')}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    toast.success('Prompt exported successfully!');
  };

  const filteredPrompts = prompts.filter(prompt =>
    prompt.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    prompt.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    prompt.model?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (isLoading) {
    return (
      <div className="prompts-loading">
        <FontAwesomeIcon icon={faSpinner} spin />
        <p>Loading prompts...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="prompts-error">
        <FontAwesomeIcon icon={faExclamationTriangle} />
        <p>Error loading prompts: {error}</p>
        <button onClick={fetchPrompts}>Retry</button>
      </div>
    );
  }

  return (
    <div className="prompts-tab">
      {/* Header */}
      <div className="prompts-header">
        <div className="header-info">
          <h2>Prompt Management</h2>
          <p>Configure AI prompts for this agent ({prompts.length} total)</p>
        </div>
        <div className="header-actions">
          <button 
            className="create-prompt-btn"
            onClick={() => setShowCreateModal(true)}
          >
            <FontAwesomeIcon icon={faPlus} />
            Create Prompt
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="prompts-search">
        <div className="search-input-wrapper">
          <FontAwesomeIcon icon={faSearch} className="search-icon" />
          <input
            type="text"
            placeholder="Search prompts by name, description, or model..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>
      </div>

      {/* Prompts Grid */}
      <div className="prompts-grid">
        {filteredPrompts.length === 0 ? (
          <div className="empty-state">
            <h3>No prompts found</h3>
            <p>
              {searchQuery 
                ? `No prompts match "${searchQuery}"`
                : 'No prompts configured for this agent'
              }
            </p>
          </div>
        ) : (
          filteredPrompts.map(prompt => (
            <div key={prompt.prompt_id} className="prompt-card">
              <div className="prompt-card-header">
                <div className="prompt-info">
                  <h3>{prompt.name}</h3>
                  <p className="prompt-description">
                    {prompt.description || 'No description available'}
                  </p>
                </div>
                <div className="prompt-actions">
                  <button
                    className="action-btn view-btn"
                    onClick={() => handleViewPrompt(prompt.prompt_id)}
                    title="View Prompt"
                  >
                    <FontAwesomeIcon icon={faEye} />
                  </button>
                  <button
                    className="action-btn edit-btn"
                    onClick={() => handleEditPrompt(prompt.prompt_id)}
                    title="Edit Prompt"
                  >
                    <FontAwesomeIcon icon={faEdit} />
                  </button>
                  <button
                    className="action-btn copy-btn"
                    onClick={() => copyToClipboard(prompt.prompt_template, 'Prompt Template')}
                    title="Copy Prompt Template"
                  >
                    <FontAwesomeIcon icon={faCopy} />
                  </button>
                  <button
                    className="action-btn export-btn"
                    onClick={() => exportPrompt(prompt)}
                    title="Export Prompt"
                  >
                    <FontAwesomeIcon icon={faDownload} />
                  </button>
                </div>
              </div>

              <div className="prompt-card-body">
                <div className="prompt-stats">
                  <div className="stat-item">
                    <span className="stat-label">Model:</span>
                    <span className="stat-value model-badge">{prompt.model}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Temperature:</span>
                    <span className="stat-value">{prompt.temperature}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Output:</span>
                    <span className={`stat-value format-badge ${prompt.output_format}`}>
                      {prompt.output_format}
                    </span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Tools:</span>
                    <span className={`stat-value tools-badge ${prompt.tool_enabled ? 'enabled' : 'disabled'}`}>
                      {prompt.tool_enabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                </div>

                <div className="prompt-card-footer">
                  <div className="prompt-date">
                    <small>
                      Updated: {formatDate(prompt.updated_at)}
                    </small>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* View Prompt Modal */}
      {showViewModal && selectedPrompt && (
        <div className="modal-overlay" onClick={() => setShowViewModal(false)}>
          <div className="modal-content view-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>View Prompt: {selectedPrompt.name}</h2>
              <button className="close-btn" onClick={() => setShowViewModal(false)}>
                ×
              </button>
            </div>
            
            <div className="modal-body">
              <div className="prompt-section">
                <h3>Basic Information</h3>
                <div className="info-grid">
                  <div className="info-item">
                    <label>Name:</label>
                    <span>{selectedPrompt.name}</span>
                  </div>
                  <div className="info-item">
                    <label>Description:</label>
                    <span>{selectedPrompt.description || 'No description'}</span>
                  </div>
                  <div className="info-item">
                    <label>Prompt ID:</label>
                    <div className="copyable-field">
                      <span className="prompt-id">{selectedPrompt.prompt_id}</span>
                      <button 
                        className="copy-small-btn"
                        onClick={() => copyToClipboard(selectedPrompt.prompt_id, 'Prompt ID')}
                      >
                        <FontAwesomeIcon icon={faCopy} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="prompt-section">
                <h3>Prompt Template</h3>
                <div className="template-display">
                  <pre>{selectedPrompt.prompt_template}</pre>
                </div>
              </div>

              <div className="prompt-section">
                <h3>Model Configuration</h3>
                <div className="config-grid">
                  <div className="config-item">
                    <label>Model:</label>
                    <span className="model-badge">{selectedPrompt.model}</span>
                  </div>
                  <div className="config-item">
                    <label>Temperature:</label>
                    <span>{selectedPrompt.temperature}</span>
                  </div>
                  <div className="config-item">
                    <label>Top P:</label>
                    <span>{selectedPrompt.top_p}</span>
                  </div>
                  <div className="config-item">
                    <label>Max Tokens:</label>
                    <span>{selectedPrompt.max_output_tokens || 'Not set'}</span>
                  </div>
                  <div className="config-item">
                    <label>Output Format:</label>
                    <span className={`format-badge ${selectedPrompt.output_format}`}>
                      {selectedPrompt.output_format}
                    </span>
                  </div>
                  <div className="config-item">
                    <label>Tools Enabled:</label>
                    <span className={`tools-badge ${selectedPrompt.tool_enabled ? 'enabled' : 'disabled'}`}>
                      {selectedPrompt.tool_enabled ? 'Yes' : 'No'}
                    </span>
                  </div>
                </div>
              </div>

              {selectedPrompt.output_schema && (
                <div className="prompt-section">
                  <h3>Output Schema</h3>
                  <div className="schema-display">
                    <pre>{JSON.stringify(selectedPrompt.output_schema, null, 2)}</pre>
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button 
                className="btn-secondary" 
                onClick={() => setShowViewModal(false)}
              >
                Close
              </button>
              <button 
                className="btn-primary"
                onClick={() => {
                  setShowViewModal(false);
                  handleEditPrompt(selectedPrompt.prompt_id);
                }}
              >
                Edit Prompt
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Prompt Modal */}
      <PromptEditor
        mode="create"
        agentId={agentId}
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSave={() => {
          fetchPrompts();
          setShowCreateModal(false);
        }}
      />

      {/* Edit Prompt Modal */}
      <PromptEditor
        mode="edit"
        prompt={selectedPrompt}
        agentId={agentId}
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSave={() => {
          fetchPrompts();
          setShowEditModal(false);
        }}
      />
    </div>
  );
};

export default PromptsTab;